import { Field, ImageField, LinkField } from "@sitecore-jss/sitecore-jss-nextjs";
import { Box, Container, Stack } from "@chakra-ui/react";
import SCHeading from "components/sc/SCHeading";
import SCText from "components/sc/SCText";
import SCImage from "components/sc/SCImage";
import SCLinkButton from "components/sc/SCLinkButton";
import BundleIcon from "components/ui/BundleIcon";
import { gradients } from "themes/foundations/color";
import { mapGradient } from "themes/utils/mapGradient";
import useImageSizes from "hooks/useImageSizes";

interface Fields {
    Kicker: Field<string>;
    Headline: Field<string>;
    Text: Field<string>;
    CTA: LinkField;
    BackgroundImageDesktop: ImageField;
    BackgroundImageMobile: ImageField;
}

type HeroProps = {
    fields: Fields;
};

interface HeroBackgroundProps extends HeroProps {
    sizes?: ReturnType<typeof useImageSizes>;
}

const HeroContent = (props: HeroProps) => {
    return (
        <Stack spacing="sp24" maxWidth={["100%", null, null, "calc((8/12) * 100%)"]}>
            <SCText
                textField={props.fields?.Kicker}
                size="eyebrowSmall"
                textTransform={"uppercase"}
            />
            <SCHeading
                as="h1"
                textField={props.fields?.Headline}
                size="h1"
                sx={{
                    wordBreak: "break-word",
                }}
            />
            <SCText size="bodyXl" textField={props.fields?.Text} />
            <SCLinkButton
                size="sm"
                rightIcon={<BundleIcon name="ArrowForward" />}
                width={"fit-content"}
                variant={"secondary"}
                linkField={props.fields?.CTA}
            />
        </Stack>
    );
};

const HeroBackground = (props: HeroBackgroundProps) => {
    const imageDesktop = props.fields?.BackgroundImageDesktop;
    const imageMobile = props.fields?.BackgroundImageMobile?.value?.src
        ? props.fields?.BackgroundImageMobile
        : props.fields?.BackgroundImageDesktop;
    return (
        <Box
            position="relative"
            _before={{
                content: '""',
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                bgGradient: mapGradient(gradients.shadowGradient),
                zIndex: 1,
                pointerEvents: "none",
                "> div:first-of-type, > img:first-of-type": {
                    display: { base: "block", lg: "none" },
                },
                "> div:last-of-type, > img:last-of-type": {
                    display: { base: "none", lg: "block" },
                },
            }}
        >
            {imageMobile && (
                <SCImage
                    priority
                    imageField={imageMobile}
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: { base: 0, lg: "base" },
                        display: { base: "block", lg: "none" },
                        minHeight: {
                            base: "calc(100svh - var(--sizes-headerMobileTopHeight) - var(--space-sp28))",
                            lg: undefined,
                        },
                    }}
                    width={Number(imageMobile?.value?.width)}
                    height={Number(imageMobile?.value?.width) * (65 / 46)}
                    sizes={props.sizes}
                />
            )}
            {imageDesktop && (
                <SCImage
                    priority
                    blur
                    imageField={imageDesktop}
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        aspectRatio: "16/9",
                        display: { base: "none", lg: "block" },
                    }}
                    width={Number(imageDesktop?.value?.width)}
                    height={Number(imageDesktop?.value?.width) * (9 / 16)}
                    sizes={props.sizes}
                />
            )}
        </Box>
    );
};

export const HeroBordered = (props: HeroProps) => {
    const sizes = useImageSizes({ base: 1 });
    return (
        <Container display={"grid"} margin={"0 auto"} variant={"hero"} alignItems={"center"}>
            <Box gridArea="1/1" width="100%" borderRadius={"base"} overflow={"hidden"}>
                <HeroBackground fields={props.fields} sizes={sizes} />
            </Box>
            <Box
                px={{ base: "sp24", lg: "sp80" }}
                gridArea="1/1"
                color="white"
                position="relative"
                zIndex={2}
            >
                <HeroContent fields={props.fields} />
            </Box>
        </Container>
    );
};
export const HeroFullScreen = (props: HeroProps) => {
    const sizes = useImageSizes({ base: 1 / 2 }, false);
    return (
        <Box
            display={"grid"}
            width={"100%"}
            placeItems={"start"}
            alignItems={"center"}
            marginTop={[
                "calc(var(--sizes-headerMobileTopHeight) * -1)",
                "calc(var(--sizes-headerTopHeight) * -1)",
            ]}
            overflow="hidden"
        >
            <Box gridArea="1/1" width="100%" maxHeight={"70dvh"}>
                <HeroBackground fields={props.fields} sizes={sizes} />
            </Box>
            <Container
                gridArea="1/1"
                color="white"
                position="relative"
                zIndex={2}
                px={"clamp(2.625rem, 0.028rem + 11.08vw, 10rem)"}
            >
                <HeroContent fields={props.fields} />
            </Container>
        </Box>
    );
};
